import { AsyncPipe, NgClass } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { AbstractControl, ControlContainer, FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import {
  NgbDropdown,
  NgbDropdownAnchor,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbTooltip,
  PlacementArray
} from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import isEqual from 'lodash/isEqual';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ButtonComponent } from '../../button';
import { TagColor } from '../../models/tag';
import { TagComponent } from '../../tag';
import { KeyCode, MbsSize } from '../../utils';
import { InputBase } from '../input-base/input-base';
import { InputErrorsComponent } from '../input-base/input-errors/input-errors.component';
import { InputLabelComponent } from '../input-base/input-label/input-label.component';
import { SuggestionType, TagInputItem, TagSuggestion } from './tags-input.types';

enum Mode {
  Collapsed = 'collapsed',
  Opened = 'opened'
}

@UntilDestroy()
@Component({
  selector: 'mbs-tags-input',
  templateUrl: './tags-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ButtonComponent,
    InputErrorsComponent,
    InputLabelComponent,
    NgClass,
    NgbDropdown,
    NgbDropdownAnchor,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    NgbTooltip,
    ReactiveFormsModule,
    TagComponent
  ],
  encapsulation: ViewEncapsulation.None
})
export class TagsInputComponent extends InputBase<TagInputItem[]> implements OnInit, OnDestroy, AfterContentInit {
  private controlContainer = inject(ControlContainer, { optional: true, host: true, skipSelf: true });
  protected ngControl: NgControl;
  protected cdRef: ChangeDetectorRef;

  public readonly modeEnum = Mode;
  public readonly suggestionType = SuggestionType;
  public readonly idPrefix = 'mbs-input-tag-';

  public control: AbstractControl;
  public isFocused = false;
  public filteredSuggestions: TagSuggestion[] = [];
  public inputControl = new FormControl('');
  public dropdownClass = '';

  private passedSuggestions: TagSuggestion[] = [];
  private observer: MutationObserver;

  public mode: Mode = Mode.Collapsed;
  public showExtendButton$: BehaviorSubject<Mode> = new BehaviorSubject(null);
  public scrollHeight = null;
  public inputControlReadonly = false;

  /** Skips adding tags from input. Tags can be added from suggestions only */
  @Input() fromSuggestionsOnly?: boolean;

  @Input() formControlName?: string;

  @Input() actionButton?: string;

  @Input() loading?: boolean;

  /**
   * Add font-weight-bold;
   */
  @Input() public boldLabel = true;

  @Input() set suggestions(suggestions: TagSuggestion[] | string[]) {
    this.passedSuggestions = suggestions.map((s) => {
      const isSuggestionString = typeof s === 'string';

      return {
        type: isSuggestionString ? SuggestionType.Plain : SuggestionType.Tag,
        name: isSuggestionString ? s : s.name,
        color: s?.color || null
      };
    });
    this.prepareSuggestions(this.inputControl.value);
  }

  @Input() resetInnerValue?: BehaviorSubject<boolean>;

  /* Need add input with dropdown */
  @Input() isStatic = false;

  @Input() isCollapseMode = false;
  @Input() collapseHeight: number;

  /* Need for empty tags wrapper */
  @Input() minHeight: number;
  @Input() inputSize: MbsSize;

  /**
   * Symbols for separate tags
   */
  @Input() separators: string[] = [','];

  @Input() addOnBlur = false;

  @Input() placement: PlacementArray = 'bottom-left bottom-right top-left top-right';

  @Output() innerValue = new EventEmitter<string>();

  @Output() addItem: EventEmitter<string> = new EventEmitter();

  @ViewChild(NgbDropdown, { static: true }) public dropdown: NgbDropdown;
  @ViewChild('inputField') public inputField: ElementRef<HTMLInputElement>;
  @ViewChild('container', { static: true }) container: ElementRef;

  constructor() {
    const ngControl = inject(NgControl, { optional: true, self: true });
    const cdRef = inject(ChangeDetectorRef, { optional: true });

    super();

    this.ngControl = ngControl;
    this.cdRef = cdRef;
  }

  ngOnInit() {
    if (this.controlContainer) {
      if (this.formControlName) {
        this.control = this.controlContainer.control.get(this.formControlName);
      } else {
        console.warn('Missing formControlName directive');
      }
    } else {
      this.control = this.ngControl.control;
    }

    this.prepareSuggestions(this.inputControl.value);

    this.inputControl.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
      this.prepareSuggestions(val);
      this.innerValue.emit(this.inputControl.valid ? val : null);
      this.dropdown?.open();
    });

    this.control.valueChanges
      .pipe(
        distinctUntilChanged((prev, current) => isEqual(prev, current)),
        untilDestroyed(this)
      )
      .subscribe((value) => {
        if (value[value.length - 1]?.name === this.inputControl?.value) {
          this.inputControl.patchValue('');
        }
      });

    this.control.statusChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.inputControlReadonly = this.control.disabled;
      this.cdRef.detectChanges();
    });

    this.resetInnerValue &&
      this.resetInnerValue.pipe(untilDestroyed(this)).subscribe((reset) => {
        reset && this.inputControl.patchValue('', { emitEvent: false });
      });

    this.inputControlReadonly = this.control.disabled;
  }

  ngOnDestroy(): void {
    this.isCollapseMode && this.observer?.disconnect();
  }

  ngAfterContentInit(): void {
    this.isCollapseMode && this.createObserver();
  }

  createObserver() {
    const callback = () => {
      this.showExtendButton$.next(null);
      this.scrollHeight = this.container.nativeElement.scrollHeight;

      if (this.scrollHeight > this.collapseHeight) {
        this.showExtendButton$.next(this.mode);
      }
    };

    this.observer?.disconnect();
    this.observer = new MutationObserver(callback);
    this.observer.observe(this.container.nativeElement, {
      attributes: true,
      childList: true,
      subtree: false
    });
  }

  writeValue(val: TagInputItem[]) {
    super.writeValue(val);
    this.value = val;
  }

  public get bindClasses(): string[] {
    return Object.entries(this.validClasses)
      .filter(([, v]) => !!v)
      .map(([k]) => k)
      .concat([this.inputSizeClass || this.sizeClass]);
  }

  private get inputSizeClass(): string {
    return this.inputSize ? 'form-control-' + this.inputSize : '';
  }

  public handleKeyDown({ target, keyCode }): void {
    if (this.disabled) return;

    if (keyCode === KeyCode.Enter && !this.fromSuggestionsOnly) {
      const value = this.getPreparedInputValue(target.value);

      if (this.inputControl.valid) {
        this.writeValue([...this.value, ...this.getPreparedUpdateValue(value)]);
        this.inputControl.patchValue('');
        this.prepareSuggestions(this.inputControl.value);
        this.inputField?.nativeElement?.focus();
      }
    }

    if (keyCode === KeyCode.BackSpace && !this.inputControl.value && this.value?.length) {
      this.writeValue(this.value.slice(0, this.value.length - 1));
      this.inputField?.nativeElement?.focus();
    }
  }

  public handleChange({ target }): void {
    if (this.disabled) return;

    if (!this.separators.some((s) => target.value.endsWith(s)) || this.fromSuggestionsOnly) return;

    if (target.value.length > 1) {
      const value = this.getPreparedInputValue(target.value);

      if (this.inputControl.valid) {
        this.writeValue([...this.value, ...this.getPreparedUpdateValue(value)]);
        this.inputField?.nativeElement?.focus();
        this.inputControl.patchValue('');
      } else {
        this.inputControl.patchValue(target.value.substring(0, target.value.length - 1));
      }
    } else {
      this.inputControl.patchValue('');
    }
  }

  private getPreparedInputValue(value: string): TagInputItem[] {
    let strArr: string[] = [value];

    this.separators.forEach((sep) => {
      const newStrArr = [];
      strArr.forEach((str) => newStrArr.push(...str.split(sep).filter(Boolean)));
      strArr = newStrArr;
    });

    return strArr.map((el) => ({ name: el.trim(), errors: [] }));
  }

  public handleClose(idx: number): void {
    const value = [...this.value.slice(0, idx), ...this.value.slice(idx + 1, this.value.length)];

    this.writeValue(value);
    this.inputField?.nativeElement?.focus();
    this.prepareSuggestions(this.inputControl.value);
  }

  public handleBlur(): void {
    if (this.addOnBlur && this.isFocused && this.inputControl.value && !this.fromSuggestionsOnly) {
      const value = this.getPreparedInputValue(this.inputControl.value);

      if (this.inputControl.valid) {
        this.writeValue([...this.value, ...this.getPreparedUpdateValue(value)]);
        this.inputControl.patchValue('');
      }
    }

    this.isFocused = false;
  }

  public handleFocus(): void {
    if (this.disabled) return;

    this.isFocused = !this.inputControlReadonly;
  }

  public onSuggestionItemClick(suggestion: TagSuggestion): void {
    if (this.disabled) return;
    let suggestionName: string;
    let color: TagColor;

    if (typeof suggestion === 'string') {
      suggestionName = suggestion;
      color = null;
    } else {
      suggestionName = suggestion.name;
      color = suggestion.color;
    }

    this.writeValue([...this.value, ...[{ name: suggestionName, errors: [], color }]]);
    this.inputField?.nativeElement?.focus();
    this.inputControl.patchValue('');
    this.prepareSuggestions(this.inputControl.value);
  }

  public onActionButtonPressed() {
    if (this.disabled) return;

    this.addItem.emit(this.inputControl.value);
    this.inputControl.patchValue('');
  }

  public isSuggestionsActionButtonVisible() {
    return this.actionButton && this.inputControl.value && !this.filteredSuggestions.map((s) => s.name).includes(this.inputControl.value);
  }

  private getPreparedUpdateValue(elements: TagInputItem[]): TagInputItem[] {
    return Array.from(new Set(elements)).map((el) => ({ name: this.trimWhiteSpaces(el.name), errors: el.errors }));
  }

  private trimWhiteSpaces(el: string) {
    return el.split(' ').filter(Boolean).join(' ');
  }

  private prepareSuggestions(val: string): void {
    this.filteredSuggestions = this.passedSuggestions.filter((s) => {
      return !(this.value || []).some((tag) => tag.name === s.name) && s.name.toLowerCase().includes(val.toLowerCase());
    });

    const needToDisplayActionButton =
      this.actionButton &&
      this.inputControl.valid &&
      this.inputControl.value &&
      !this.value?.some((item) => item.name === this.inputControl.value) &&
      !this.passedSuggestions?.map((s) => s.name).includes(this.inputControl.value);

    this.dropdownClass = this.filteredSuggestions.length || needToDisplayActionButton ? '' : 'mbs-tags-input_dropdown-empty';
  }
}
